import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { Room } from '../models/rooms'

const ROOM_ATTACH_UNAVAILABLE_MESSAGE = "This room can't be attached to the Outlook event."
const ROOM_ATTACH_FAILED_MESSAGE = 'Failed to attach the selected room to the Outlook event.'

function getComposeAppointmentItem(): Office.AppointmentCompose | null {
    const item = Office.context?.mailbox?.item

    if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
        return null
    }

    return item as Office.AppointmentCompose
}

function addItemHandler(eventType: Office.EventType, handler: () => void): Promise<void> {
    return new Promise((resolve, reject) => {
        const item = Office.context?.mailbox?.item

        if (!item) {
            resolve()
            return
        }

        item.addHandlerAsync(eventType, handler, (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve()
                return
            }

            reject(result.error)
        })
    })
}

function setLocationAsync(location: Office.Location, value: string): Promise<void> {
    return new Promise((resolve, reject) => {
        location.setAsync(value, (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve()
                return
            }

            reject(result.error)
        })
    })
}

function getEnhancedLocationsAsync(enhancedLocation: Office.EnhancedLocation): Promise<Office.LocationDetails[]> {
    return new Promise((resolve, reject) => {
        enhancedLocation.getAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve(result.value || [])
                return
            }

            reject(result.error)
        })
    })
}

function addEnhancedLocationsAsync(
    enhancedLocation: Office.EnhancedLocation,
    locations: Office.LocationIdentifier[],
): Promise<void> {
    return new Promise((resolve, reject) => {
        enhancedLocation.addAsync(locations, (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve()
                return
            }

            reject(result.error)
        })
    })
}

function removeEnhancedLocationsAsync(
    enhancedLocation: Office.EnhancedLocation,
    locations: Office.LocationIdentifier[],
): Promise<void> {
    return new Promise((resolve, reject) => {
        enhancedLocation.removeAsync(locations, (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve()
                return
            }

            reject(result.error)
        })
    })
}

function createEmailSet(rooms: Room[]) {
    return new Set(rooms.map((room) => room.outlookRoomEmail?.toLowerCase()).filter(Boolean))
}

type UseRoomAttachmentArgs = {
    enabled: boolean
    allMappedRooms: Room[]
}

export function useRoomAttachment({ enabled, allMappedRooms }: UseRoomAttachmentArgs) {
    const locationChangeHandlerBound = useRef(false)
    const mappedRoomsRef = useRef<Room[]>([])
    const [attachedRoomId, setAttachedRoomId] = useState<string | null>(null)
    const [attachError, setAttachError] = useState<string | null>(null)
    const [attachingRoomId, setAttachingRoomId] = useState<string | null>(null)

    const mappedRoomEmails = useMemo(() => createEmailSet(allMappedRooms), [allMappedRooms])

    useEffect(() => {
        mappedRoomsRef.current = allMappedRooms
    }, [allMappedRooms])

    const syncAttachedRoomFromOutlook = useCallback(async (mappedRooms: Room[]) => {
        const appointment = getComposeAppointmentItem()

        if (!appointment || !mappedRooms.length) {
            setAttachedRoomId(null)
            return
        }

        try {
            if (Office.context.requirements.isSetSupported('Mailbox', '1.8')) {
                const locations = await getEnhancedLocationsAsync(appointment.enhancedLocation)
                const locationIds = new Set(
                    locations.map((location) => location.locationIdentifier.id?.toLowerCase()).filter(Boolean),
                )
                const attachedRoomByLocation = mappedRooms.find((room) =>
                    room.outlookRoomEmail ? locationIds.has(room.outlookRoomEmail.toLowerCase()) : false,
                )

                if (attachedRoomByLocation) {
                    setAttachedRoomId(attachedRoomByLocation.id)
                    return
                }
            }

            appointment.location.getAsync((result) => {
                if (result.status !== Office.AsyncResultStatus.Succeeded) {
                    setAttachedRoomId(null)
                    return
                }

                const normalizedLocation = (result.value || '').toLowerCase()
                const attachedRoomByText = mappedRooms.find(
                    (room) =>
                        (room.outlookRoomName && normalizedLocation.includes(room.outlookRoomName.toLowerCase())) ||
                        (room.name && normalizedLocation.includes(room.name.toLowerCase())),
                )

                setAttachedRoomId(attachedRoomByText?.id || null)
            })
        } catch (error) {
            console.error('Failed to read attached room from Outlook.', error)
        }
    }, [])

    useEffect(() => {
        if (!enabled) {
            return
        }

        void syncAttachedRoomFromOutlook(allMappedRooms)
    }, [allMappedRooms, enabled, syncAttachedRoomFromOutlook])

    useEffect(() => {
        if (!enabled || locationChangeHandlerBound.current || !Office.context.requirements.isSetSupported('Mailbox', '1.8')) {
            return
        }

        const appointment = getComposeAppointmentItem()
        if (!appointment) {
            return
        }

        void addItemHandler(Office.EventType.EnhancedLocationsChanged, () => {
            void syncAttachedRoomFromOutlook(mappedRoomsRef.current)
        })
            .then(() => {
                locationChangeHandlerBound.current = true
            })
            .catch((error) => {
                console.error('Failed to subscribe to Outlook location changes.', error)
            })
    }, [enabled, syncAttachedRoomFromOutlook])

    const attachMappedRoomToAppointment = useCallback(
        async (roomId: string) => {
            const appointment = getComposeAppointmentItem()
            const targetRoom = allMappedRooms.find((room) => room.id === roomId)

            if (!appointment || !targetRoom?.outlookRoomEmail) {
                setAttachError(ROOM_ATTACH_UNAVAILABLE_MESSAGE)
                return
            }

            setAttachError(null)
            setAttachingRoomId(roomId)

            try {
                if (Office.context.requirements.isSetSupported('Mailbox', '1.8')) {
                    try {
                        const existingLocations = await getEnhancedLocationsAsync(appointment.enhancedLocation)
                        const mappedLocationIds = existingLocations
                            .map((location) => location.locationIdentifier)
                            .filter(
                                (location) =>
                                    location.id &&
                                    mappedRoomEmails.has(location.id.toLowerCase()) &&
                                    location.type === Office.MailboxEnums.LocationType.Room,
                            )

                        if (mappedLocationIds.length) {
                            await removeEnhancedLocationsAsync(appointment.enhancedLocation, mappedLocationIds)
                        }

                        await addEnhancedLocationsAsync(appointment.enhancedLocation, [
                            {
                                id: targetRoom.outlookRoomEmail,
                                type: Office.MailboxEnums.LocationType.Room,
                            },
                        ])
                    } catch (error) {
                        console.error('Failed to update enhanced location.', error)
                    }
                }

                await setLocationAsync(appointment.location, targetRoom.outlookRoomName || targetRoom.name)
                setAttachedRoomId(targetRoom.id)
            } catch (error) {
                console.error('Failed to attach room to Outlook appointment.', error)
                setAttachError(ROOM_ATTACH_FAILED_MESSAGE)
            } finally {
                setAttachingRoomId(null)
            }
        },
        [allMappedRooms, mappedRoomEmails],
    )

    return {
        attachedRoomId,
        attachError,
        attachingRoomId,
        attachMappedRoomToAppointment,
    }
}
